/**
 * QuoteEditView Component - Form for editing quotes (similar to QuoteForm)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import { ArrowBack, Save, Add, Delete } from "@mui/icons-material";
import { getQuoteById, updateQuoteContent } from "../services/quoteService.js";
import CustomerSearchAutocomplete from "../../../shared/components/CustomerSearchAutocomplete.jsx";
import LeadSearchAutocomplete from "../../../shared/components/LeadSearchAutocomplete.jsx";
import OrderSearchAutocomplete from "../../../shared/components/OrderSearchAutocomplete.jsx";
import PriceCardLineItemEditor from "../../priceCard/components/PriceCardLineItemEditor.jsx";
import { clearPriceCardSnapshot } from "../../priceCard/utils/lineItemPricing.js";
import SupplierSelect from "../../../shared/components/SupplierSelect.jsx";
import CreateProductModal from "../../products/components/CreateProductModal.jsx";
import api from "../../../shared/api.jsx";
import { updateLead } from "../../leads/repositories/leadRepository.js";
import { formatCurrency } from "../../../shared/utils/formatters.js";
import { logError, logInfo, logWarn } from "../../../shared/utils/logger.js";
import {
  formatLeadItemsList,
  resolveBasicItemsListString,
} from "../../../shared/utils/leadItems.js";
import { useItems } from "../../../hooks/useItems.js";
import { GoogleAddressAutocomplete } from "../../../components/AddressInput/GoogleAddressAutocomplete.jsx";
import { repriceAutomatedPreparation, resolveQuoteDisplayTotal } from "../services/automatedPreparationReviewService.js";
import EstimatePricingFab from "../components/EstimatePricingFab.jsx";
import { formatDisplayError } from "../../../shared/utils/displayError.js";

function toDateInputValue(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const ymd = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * QuoteEditView Component
 */
const QuoteEditView = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [warning, setWarning] = useState(null);
  const [success, setSuccess] = useState(false);
  const errorAlertRef = useRef(null);

  const showError = useCallback((value, fields = {}) => {
    setSuccess(false);
    setError(formatDisplayError(value, "Failed to update quote"));
    setFieldErrors(fields);
  }, []);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [quote, setQuote] = useState(null);

  // Fetch basic items from Lead via itemSetId
  const { items: fetchedItems, loading: itemsLoading, error: itemsError, refetch: refetchItems } = useItems(
    quote?.itemSetId,
    [] // Don't use quote.items as fallback - those are line items
  );

  const [formData, setFormData] = useState({
    customer: null,
    address: "",
    from: "",
    to: "",
    pickupAddressData: null,
    deliveryAddressData: null,
    date: "",
    dates: [""],
    category: "moving",
    items: "", // Basic items list (from Lead via itemSetId)
    lineItems: [], // Line items with pricing (quote.items)
    notes: "",
    estimatedCost: "",
    status: "pending",
    truckSize: "",
    loadType: "",
    serviceType: "",
    jobId: "", // Link to job/order
  });

  const [createProductModal, setCreateProductModal] = useState({
    open: false,
    index: null,
    name: "",
  });

  const openCreateProductModal = (index, name) => {
    setCreateProductModal({ open: true, index, name });
  };

  const closeCreateProductModal = () => {
    setCreateProductModal({ open: false, index: null, name: "" });
  };

  // Helper function to calculate total from line items
  const calculateItemsTotal = (items) => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 1;
      const unitPrice = Number(item.unitPrice) || 0;
      return sum + (item.total || quantity * unitPrice || 0);
    }, 0);
  };

  // Add new line item
  const addLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [
        ...formData.lineItems,
        {
          product: null,
          productId: null,
          description: "",
          quantity: 1,
          unitPrice: 0,
          total: 0,
          supplier: "bubakii",
          isCustom: true,
          from: "",
          to: "",
        },
      ],
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    const newItems = formData.lineItems.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      lineItems: newItems,
    });
  };

  // Handle line item change
  const handleLineItemChange = (index, field) => (e) => {
    const newItems = [...formData.lineItems];
    const value = e.target ? e.target.value : e; // Support both event and direct value

    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // Recalculate total if unitPrice or quantity changed
    if (field === "unitPrice" || field === "quantity") {
      const quantity = Number(newItems[index].quantity) || 1;
      const unitPrice = Number(newItems[index].unitPrice || 0);
      newItems[index].total = quantity * unitPrice;
    }

    setFormData({
      ...formData,
      lineItems: newItems,
    });
  };

  const replaceLineItem = (index) => (lineItem) => {
    const newItems = [...formData.lineItems];
    newItems[index] = lineItem;
    setFormData({ ...formData, lineItems: newItems });
  };

  // Handle product selection
  const handleProductSelect = (index) => (product) => {
    const newItems = [...formData.lineItems];
    newItems[index] = {
      ...clearPriceCardSnapshot(newItems[index]),
      product: product,
      productId: product?.id || null,
      description: product?.name || newItems[index].description,
      unitPrice: product?.unitPrice || newItems[index].unitPrice,
      isCustom: !product,
      // Auto-populate supplier from product defaultSupplier
      supplier:
        product?.defaultSupplier || newItems[index].supplier || "bubakii",
    };

    // Recalculate total
    const quantity = Number(newItems[index].quantity) || 1;
    const unitPrice = Number(newItems[index].unitPrice || 0);
    newItems[index].total = quantity * unitPrice;

    setFormData({
      ...formData,
      lineItems: newItems,
    });
  };

  // Handle custom item entry
  const handleCustomItem = (index) => (description) => {
    const newItems = [...formData.lineItems];
    newItems[index] = {
      ...newItems[index],
      product: null,
      productId: null,
      description: description,
      isCustom: true,
    };

    setFormData({
      ...formData,
      lineItems: newItems,
    });
  };

  useEffect(() => {
    if (!error || !errorAlertRef.current) return;
    errorAlertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [error]);

  // Load quote data
  const loadQuote = useCallback(async () => {
    if (!quoteId) {
      showError("Invalid quote identifier");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});
      setWarning(null);

      const response = await getQuoteById(quoteId);
      if (response?.success === false) {
        throw new Error(
          formatDisplayError(response, "Failed to load quote")
        );
      }

      const quoteData =
        response?.data?.data ||
        response?.data ||
        response?.quote ||
        response ||
        null;

      if (!quoteData || !quoteData.id) {
        throw new Error("Quote data not found");
      }

      setQuote(quoteData);

      // Debug logging for quote data
      logInfo("QuoteEditView: Quote loaded", {
        quoteId: quoteData.id,
        itemSetId: quoteData.itemSetId,
        hasItems: !!quoteData.items,
        itemsCount: quoteData.items?.length || 0,
        itemsType: Array.isArray(quoteData.items) ? "array" : typeof quoteData.items,
        leadId: quoteData.leadId,
      });

      // Populate form with quote data (date inputs require YYYY-MM-DD)
      const rawDates =
        quoteData.dates && quoteData.dates.length > 0
          ? quoteData.dates
          : quoteData.date
            ? [quoteData.date]
            : [""];
      const datesArray = rawDates.map((d) => toDateInputValue(d) || "");

      // NOTE: quoteData.items contains priced LINE ITEMS; basic items come from leadItems/itemSetId
      const basicItemsString = resolveBasicItemsListString(quoteData);

      // Load line items from quoteData.items
      // Convert quote line items to form format
      const lineItemsArray =
        Array.isArray(quoteData.items) && quoteData.items.length > 0
          ? quoteData.items.map((item) => ({
              product: null, // Product object will be loaded if productId exists
              productId: item.productId || null,
              priceCardElementId: item.priceCardElementId || null,
              priceCardId: item.priceCardId || null,
              priceCardVersion: item.priceCardVersion || null,
              unit: item.unit || null,
              description: item.description || "",
              quantity: item.quantity || 1,
              unitPrice: item.unitPrice || 0,
              total: item.total || (item.quantity || 1) * (item.unitPrice || 0),
              supplier: item.supplier || "bubakii",
              isCustom: item.isCustom !== false, // Default to true if not set
              from: item.from || "",
              to: item.to || "",
            }))
          : []; // Start with empty array - form hidden by default

      // Set customer object if customerId exists
      const customer = quoteData.customerId
        ? {
            customerId: quoteData.customerId,
            _id: quoteData.customerId,
            name: quoteData.customerName || "",
            phone: quoteData.customerPhone || "",
            email: quoteData.customerEmail || null,
            address: quoteData.address || null,
          }
        : null;

      let pickupAddressData = quoteData.pickupAddress || null;
      let deliveryAddressData = quoteData.deliveryAddress || null;
      let from =
        quoteData.from ||
        pickupAddressData?.address ||
        "";
      let to =
        quoteData.to ||
        deliveryAddressData?.address ||
        "";
      let leadServiceType = "";

      // Load lead if leadId exists (also used to backfill structured addresses)
      if (quoteData.leadId) {
        try {
          const leadResponse = await api.get(`/leads/${quoteData.leadId}`);
          const lead =
            leadResponse.data?.data || leadResponse.data || leadResponse;
          setSelectedLead(lead);
          leadServiceType = lead?.qualification?.serviceType || "";
          pickupAddressData =
            pickupAddressData || lead.pickupAddress || null;
          deliveryAddressData =
            deliveryAddressData || lead.deliveryAddress || null;
          from =
            from ||
            lead.pickupAddress?.address ||
            lead.moving_from ||
            "";
          to =
            to ||
            lead.deliveryAddress?.address ||
            lead.moving_to ||
            "";
        } catch (err) {
          logError("QuoteEditView: failed to load lead", err);
        }
      }

      setFormData({
        customer: customer,
        address: quoteData.address || "",
        from,
        to,
        pickupAddressData,
        deliveryAddressData,
        date: quoteData.date || "",
        dates: datesArray,
        category: quoteData.category || "moving",
        items: basicItemsString,
        lineItems: lineItemsArray, // Load line items from quote
        notes: quoteData.notes || "",
        estimatedCost: quoteData.estimatedCost
          ? String(quoteData.estimatedCost)
          : "",
        status: quoteData.status || "pending",
        truckSize: quoteData.truckSize || "",
        loadType: quoteData.loadType || "",
        serviceType:
          leadServiceType ||
          quoteData.serviceType ||
          (quoteData.loadType === "share_load" ? "shared_load" : "") ||
          "",
        jobId: quoteData.jobId || "",
      });

      logInfo("QuoteEditView: quote loaded", { quoteId });
    } catch (err) {
      logError("QuoteEditView: failed to load quote", err);
      showError(err, {});
    } finally {
      setLoading(false);
    }
  }, [quoteId, showError]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  // Fallback: populate items only when still empty (avoids race with loadQuote and user edits)
  useEffect(() => {
    if (itemsError) {
      logError("QuoteEditView: Failed to fetch items", {
        itemSetId: quote?.itemSetId,
        error: itemsError,
      });
      return;
    }

    if (itemsLoading) {
      return;
    }

    setFormData((prev) => {
      if (prev.items?.trim()) {
        return prev;
      }

      if (fetchedItems?.length > 0) {
        const itemsString = formatLeadItemsList(fetchedItems);
        if (itemsString) {
          logInfo("QuoteEditView: Populating items from fetchedItems", {
            itemCount: fetchedItems.length,
            itemsString,
          });
          return { ...prev, items: itemsString };
        }
      }

      if (quote && !quote.itemSetId && quote.items?.length > 0) {
        const itemsString = formatLeadItemsList(quote.items);
        if (itemsString) {
          logInfo(
            "QuoteEditView: Using fallback - extracting items from line items",
            { lineItemsCount: quote.items.length }
          );
          return { ...prev, items: itemsString };
        }
      }

      if (quote && !quote.itemSetId && !quote.items?.length) {
        logWarn(
          "QuoteEditView: No items found - quote has no itemSetId and no line items",
          {
            quoteId: quote.id,
            hasItemSetId: !!quote.itemSetId,
            hasItems: !!quote.items,
            itemsCount: quote.items?.length || 0,
          }
        );
      }

      return prev;
    });
  }, [fetchedItems, quote, itemsLoading, itemsError]);

  // GoogleAddressAutocomplete passes the string value, not a DOM event.
  const handleChange = (field) => (e) => {
    const value =
      typeof e === "string" || typeof e === "number"
        ? e
        : e?.target?.type === "checkbox"
          ? e.target.checked
          : e?.target?.value;
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };
      // Typing over a selected place invalidates structured address data
      if (field === "from") {
        next.pickupAddressData = null;
      }
      if (field === "to") {
        next.deliveryAddressData = null;
      }
      return next;
    });
    setError(null);
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePlaceSelect = (type) => (addressData) => {
    setError(null);
    setFieldErrors((prev) => ({
      ...prev,
      [type === "pickup" ? "from" : "to"]: undefined,
    }));
    setFormData((prev) => ({
      ...prev,
      [type === "pickup" ? "from" : "to"]: addressData.address,
      [`${type === "pickup" ? "pickup" : "delivery"}AddressData`]: addressData,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setWarning(null);
    setSuccess(false);

    // Validate required fields
    if (!formData.customer) {
      showError("Customer is required", { customer: "Customer is required" });
      setSaving(false);
      return;
    }

    // Validate at least one date is provided
    const datesArray = (
      Array.isArray(formData.dates)
        ? formData.dates
        : formData.date
          ? [formData.date]
          : [""]
    ).map((d) => toDateInputValue(d));
    const validDates = datesArray.filter((d) => d && d.trim() !== "");
    if (validDates.length === 0) {
      showError("At least one move date is required", {
        dates: "At least one move date is required",
      });
      setSaving(false);
      return;
    }

    const routeFrom =
      (formData.pickupAddressData?.address &&
        String(formData.pickupAddressData.address).trim()) ||
      (formData.from && String(formData.from).trim()) ||
      "";
    const routeTo =
      (formData.deliveryAddressData?.address &&
        String(formData.deliveryAddressData.address).trim()) ||
      (formData.to && String(formData.to).trim()) ||
      "";
    if (!routeFrom || !routeTo) {
      showError("Moving from and moving to addresses are required", {
        from: routeFrom ? undefined : "Pickup address is required",
        to: routeTo ? undefined : "Destination address is required",
      });
      setSaving(false);
      return;
    }

    // Validate line items if provided (line items are optional)
    const validLineItems = formData.lineItems.filter(
      (item) =>
        item.description &&
        item.description.trim().length > 0 &&
        item.unitPrice !== undefined &&
        item.unitPrice !== null &&
        item.unitPrice >= 0 &&
        item.supplier &&
        item.supplier.trim().length > 0
    );
    if (
      validLineItems.some(
        (item) =>
          item.pricingOverride?.applied === true &&
          !item.pricingOverride?.reason?.trim(),
      )
    ) {
      setError("A reason is required for every price-card override");
      setSaving(false);
      return;
    }

    // If items are provided, validate they have required fields
    if (formData.lineItems && formData.lineItems.length > 0) {
      // Validate all items have supplier
      const itemsWithoutSupplier = validLineItems.filter(
        (item) => !item.supplier
      );
      if (itemsWithoutSupplier.length > 0) {
        showError("All line items must have a supplier selected");
        setSaving(false);
        return;
      }
    }

    // Convert line items to quote items format
    // Only include valid items if any are provided (line items are optional)
    const itemsArray =
      validLineItems.length > 0
        ? validLineItems.map((item) => ({
            type: "text",
            description: item.description.trim(),
            quantity: Number(item.quantity) || 1,
            productId: item.productId || null,
            priceCardElementId: item.priceCardElementId || null,
            priceCardId: item.priceCardId || null,
            priceCardVersion: item.priceCardVersion || null,
            unit: item.unit || null,
            pricingOverride: item.pricingOverride || undefined,
            isCustom: item.isCustom !== false, // Default to true if not set
            supplier: item.supplier || "bubakii", // Required field
            unitPrice: item.unitPrice ? Number(item.unitPrice) : undefined,
            total: item.total ? Number(item.total) : undefined,
          }))
        : [];

    // Calculate final estimated cost: items total + manual override (if provided)
    const itemsTotal = calculateItemsTotal(validLineItems);
    const finalEstimatedCost = resolveQuoteDisplayTotal(
      itemsTotal,
      formData.estimatedCost
    );

    // Prepare update data - only include fields allowed by backend
    // Allowed fields: estimatedCost, date, dates, notes, from, to, customerName, customerPhone, items, loadType, orderId
    const contentUpdates = {
      customerName: formData.customer.name || "",
      from: routeFrom,
      to: routeTo,
      dates: validDates,
      items: itemsArray, // LINE ITEMS with pricing
      notes: formData.notes || "",
      estimatedCost: finalEstimatedCost,
    };

    // Add orderId/jobId if selected (backend accepts orderId in contentUpdates)
    if (formData.jobId) {
      contentUpdates.orderId = formData.jobId;
    }

    // Handle customerPhone - only include if it exists and is valid
    // Backend validation allows null, empty string, or valid phone format
    // Skip TEMP_ placeholder phones (they don't match the validation regex)
    if (formData.customer.phone) {
      const phoneStr = String(formData.customer.phone).trim();
      // Check if it's a TEMP_ placeholder or matches valid phone format
      // Valid format: /^\+?[\d\s\-()]+$/ (optional +, digits, spaces, hyphens, parentheses)
      if (phoneStr && !phoneStr.startsWith("TEMP_")) {
        // Validate it matches the expected format
        const phoneRegex = /^\+?[\d\s\-()]+$/;
        if (phoneRegex.test(phoneStr)) {
          contentUpdates.customerPhone = phoneStr;
        }
        // If it doesn't match, we skip it (send null implicitly)
      }
    }

    // Add date field for backward compatibility (first date from dates array)
    if (contentUpdates.dates.length > 0) {
      contentUpdates.date = contentUpdates.dates[0];
    }

    // Add loadType if present
    if (formData.loadType) {
      contentUpdates.loadType = formData.loadType;
    }

    // Parse basic items string into array format for backend
    // Only include if quote has itemSetId (linked to Lead)
    if (quote?.itemSetId && formData.items) {
      const itemsString = formData.items.trim();
      if (itemsString) {
        const parseItemString = (itemStr) => {
          const trimmed = itemStr.trim();
          if (!trimmed) return null;

          const match = trimmed.match(/^(\d+)\s+(.+)$/);
          if (match) {
            return {
              type: "text",
              content: match[2].trim(),
              quantity: parseInt(match[1], 10),
            };
          }
          return {
            type: "text",
            content: trimmed,
          };
        };

        const listItemsArray = itemsString
          .split(",")
          .map((item) => parseItemString(item))
          .filter((item) => item && item.content);

        if (listItemsArray.length > 0) {
          contentUpdates.listItems = listItemsArray;
        }
      }
    }

    try {
      const response = await updateQuoteContent(
        quoteId,
        contentUpdates,
        "Admin update"
      );

      if (response?.success === false) {
        throw new Error(
          formatDisplayError(response, "Failed to update quote")
        );
      }

      const leadId = quote?.leadId || selectedLead?.id || selectedLead?._id;
      if (leadId && formData.serviceType) {
        try {
          await updateLead(leadId, {
            qualification: { serviceType: formData.serviceType },
          });
          await repriceAutomatedPreparation(quoteId);
        } catch (repriceErr) {
          logWarn("QuoteEditView: lead service type / reprice failed", {
            quoteId,
            leadId,
            error: repriceErr.message,
          });
          setWarning(
            formatDisplayError(
              repriceErr,
              "Quote saved, but re-run pricing failed. Stay here or open the quote and re-run pricing."
            )
          );
          setSuccess(true);
          setSaving(false);
          return;
        }
      }

      setSuccess(true);
      logInfo("QuoteEditView: quote updated", { quoteId });

      // Refetch items to ensure we have the latest before navigating
      if (quote?.itemSetId) {
        await refetchItems();
      }

      setTimeout(() => {
        navigate(`/quotes/${quoteId}`);
      }, 1500);
    } catch (err) {
      logError("QuoteEditView: failed to update quote", err);
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = useCallback(() => {
    const previous = location.state?.from;
    if (previous?.pathname) {
      navigate(`${previous.pathname}${previous.search || ""}`);
    } else if (typeof previous === "string") {
      navigate(previous);
    } else {
      navigate(`/quotes/${quoteId}`);
    }
  }, [navigate, location.state, quoteId]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !quote) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {formatDisplayError(error, "Failed to load quote")}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4">Edit Quote {quoteId}</Typography>
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Box ref={errorAlertRef} sx={{ mb: 2 }}>
              <Alert
                severity="error"
                onClose={() => {
                  setError(null);
                  setFieldErrors({});
                }}
              >
                {formatDisplayError(error, "Failed to update quote")}
              </Alert>
            </Box>
          )}

          {warning && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              onClose={() => setWarning(null)}
            >
              {formatDisplayError(warning)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {warning
                ? "Quote updated, but pricing was not refreshed."
                : "Quote updated successfully! Redirecting..."}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Lead Search Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Link to Existing Lead (Optional)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Search for an existing lead to link to this quote. You can
                  search by customer name, phone number, or email.
                </Typography>
                <LeadSearchAutocomplete
                  value={selectedLead}
                  showQuotedStatus={true}
                  onChange={(lead) => {
                    setSelectedLead(lead);
                  }}
                />
                {selectedLead && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Lead linked to this quote.
                  </Alert>
                )}
              </Grid>

              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <CustomerSearchAutocomplete
                  value={formData.customer}
                  onChange={(customer) => {
                    setFormData({
                      ...formData,
                      customer: customer,
                      address: customer?.address || formData.address,
                    });
                    setError(null);
                  }}
                  label="Customer *"
                  required
                  error={fieldErrors.customer || null}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleChange("address")}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Job/Order Link */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Link to Job/Order (Optional)
                </Typography>
                <OrderSearchAutocomplete
                  value={selectedOrder}
                  selectedOrderId={formData.jobId || quote?.jobId || null}
                  restrictToCustomer={false}
                  onChange={(order) => {
                    setSelectedOrder(order);
                    setFormData((prev) => ({
                      ...prev,
                      jobId: order?.id || order?.jobId || "",
                    }));
                    setError(null);
                  }}
                  customerId={formData.customer?.customerId || formData.customer?._id || null}
                  label="Order / Job (Optional)"
                />
              </Grid>

              {/* Lead Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Lead Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <GoogleAddressAutocomplete
                  label="Moving From"
                  value={formData.from}
                  onChange={handleChange("from")}
                  onPlaceSelect={handlePlaceSelect("pickup")}
                  placeholder="Start typing your pickup address..."
                  error={fieldErrors.from || null}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <GoogleAddressAutocomplete
                  label="Moving To"
                  value={formData.to}
                  onChange={handleChange("to")}
                  onPlaceSelect={handlePlaceSelect("delivery")}
                  placeholder="Start typing your destination address..."
                  error={fieldErrors.to || null}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Move Dates *
                </Typography>
                {(Array.isArray(formData.dates) ? formData.dates : [""]).map(
                  (date, index) => (
                    <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                      <Grid item xs={11}>
                        <TextField
                          fullWidth
                          label={`Date ${index + 1}`}
                          type="date"
                          value={toDateInputValue(date)}
                          onChange={(e) => {
                            const currentDates = Array.isArray(formData.dates)
                              ? formData.dates
                              : [""];
                            const newDates = [...currentDates];
                            newDates[index] = toDateInputValue(e.target.value);
                            setFormData({ ...formData, dates: newDates });
                            setFieldErrors((prev) => ({
                              ...prev,
                              dates: undefined,
                            }));
                            setError(null);
                          }}
                          error={Boolean(fieldErrors.dates) && index === 0}
                          helperText={
                            index === 0 ? fieldErrors.dates || undefined : undefined
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        {(Array.isArray(formData.dates) ? formData.dates : [""])
                          .length > 1 && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              const currentDates = Array.isArray(formData.dates)
                                ? formData.dates
                                : [""];
                              const newDates = currentDates.filter(
                                (_, i) => i !== index
                              );
                              setFormData({ ...formData, dates: newDates });
                            }}
                            sx={{ mt: 1, minWidth: "auto" }}
                          >
                            ×
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  )
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    const currentDates = Array.isArray(formData.dates)
                      ? formData.dates
                      : [""];
                    setFormData({
                      ...formData,
                      dates: [...currentDates, ""],
                    });
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Another Date
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={formData.category}
                  onChange={handleChange("category")}
                >
                  <MenuItem value="moving">Moving</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                  <MenuItem value="storage">Storage</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Items *"
                  value={formData.items}
                  onChange={handleChange("items")}
                  placeholder="Comma-separated list (e.g., Sofa, Table, Chairs)"
                  helperText="Separate multiple items with commas"
                  multiline
                  minRows={3}
                  maxRows={10}
                  required
                  sx={{
                    "& .MuiInputBase-root": {
                      resize: "vertical",
                    },
                  }}
                />
              </Grid>

              {/* Line Items Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Line Items
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Add products from the catalog or enter custom items. Each line
                  item must have a description, unit price, and supplier
                  selected.
                </Typography>
              </Grid>

              {formData.lineItems.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                    <Card
                      variant="outlined"
                      sx={{ p: 2, mb: 2, overflow: "visible" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          Line Item {index + 1}
                        </Typography>
                        <IconButton
                          onClick={() => removeLineItem(index)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <PriceCardLineItemEditor
                            lineItem={item}
                            onLineItemChange={replaceLineItem(index)}
                            onCreateProduct={(name) =>
                              openCreateProductModal(index, name)
                            }
                            label="Tariff, Product or Custom Item"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description *"
                            value={item.description}
                            onChange={handleLineItemChange(
                              index,
                              "description"
                            )}
                            required
                            placeholder="Item description"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Quantity *"
                            type="number"
                            value={item.quantity || 1}
                            onChange={handleLineItemChange(index, "quantity")}
                            required
                            inputProps={{ min: 1, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Unit Price (R) *"
                            type="number"
                            value={item.unitPrice}
                            onChange={handleLineItemChange(index, "unitPrice")}
                            required
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <SupplierSelect
                            value={item.supplier || "bubakii"}
                            onChange={(supplier) => {
                              const newItems = [...formData.lineItems];
                              newItems[index] = {
                                ...newItems[index],
                                supplier: supplier,
                              };
                              setFormData({
                                ...formData,
                                lineItems: newItems,
                              });
                            }}
                            defaultSupplier={item.product?.defaultSupplier}
                            label="Supplier *"
                            required
                          />
                        </Grid>
                        {item.total > 0 && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Total"
                              value={formatCurrency(item.total || 0)}
                              disabled
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                </React.Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  startIcon={<Add />}
                  onClick={addLineItem}
                  variant="outlined"
                  size="small"
                >
                  Add Line Item
                </Button>
              </Grid>

              {/* SOP #1: Truck Size Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Truck Size
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Truck Size"
                  value={formData.truckSize}
                  onChange={handleChange("truckSize")}
                  helperText="Select the appropriate truck size for this move"
                >
                  <MenuItem value="">None selected</MenuItem>
                  <MenuItem value="1_ton">1 Ton</MenuItem>
                  <MenuItem value="2_ton">2 Ton</MenuItem>
                  <MenuItem value="4_ton">4 Ton</MenuItem>
                  <MenuItem value="8_ton">8 Ton</MenuItem>
                  <MenuItem value="1_ton_trailer">1 Ton with Trailer</MenuItem>
                  <MenuItem value="2_ton_trailer">2 Ton with Trailer</MenuItem>
                  <MenuItem value="4_ton_trailer">4 Ton with Trailer</MenuItem>
                  <MenuItem value="8_ton_trailer">8 Ton with Trailer</MenuItem>
                </TextField>
              </Grid>

              {(selectedLead || quote?.leadId) && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Service type"
                    value={formData.serviceType}
                    onChange={handleChange("serviceType")}
                    helperText="Shared load vs Exclusive load. Changing this updates the lead and should be followed by Re-run pricing."
                  >
                    <MenuItem value="">Select service type</MenuItem>
                    <MenuItem value="shared_load">Shared load</MenuItem>
                    <MenuItem value="exclusive_load">Exclusive load</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* SOP #2: Load Type */}
              {(selectedLead || quote?.leadId) && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Load Type"
                    value={formData.loadType}
                    onChange={handleChange("loadType")}
                    helperText="Select the load type for this quote (SOP #2)"
                  >
                    <MenuItem value="">Select load type</MenuItem>
                    <MenuItem value="small_load">Small Load</MenuItem>
                    <MenuItem value="medium_load">Medium Load</MenuItem>
                    <MenuItem value="large_load">Large Load</MenuItem>
                    <MenuItem value="full_load">Full Load</MenuItem>
                    <MenuItem value="share_load">Shared load</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* Quote Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Quote Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Cost"
                  type="number"
                  value={formData.estimatedCost}
                  onChange={handleChange("estimatedCost")}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Optional — enter manually or adopt from Quote Prep"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={formData.status}
                  onChange={handleChange("status")}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="negotiating">Negotiating</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                  placeholder="Additional notes about this quote..."
                />
              </Grid>

              {/* Total Cost Display */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    bgcolor: "primary.50",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      Total Cost:
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(
                        resolveQuoteDisplayTotal(
                          calculateItemsTotal(formData.lineItems),
                          formData.estimatedCost
                        )
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      gap: 2,
                      fontSize: "0.875rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Items Total:{" "}
                      <strong>
                        {formatCurrency(
                          calculateItemsTotal(formData.lineItems)
                        )}
                      </strong>
                    </Typography>
                    {formData.estimatedCost &&
                      parseFloat(formData.estimatedCost) > 0 &&
                      parseFloat(formData.estimatedCost) !==
                        calculateItemsTotal(formData.lineItems) && (
                        <Typography variant="body2" color="text.secondary">
                          Quote total override:{" "}
                          <strong>
                            {formatCurrency(parseFloat(formData.estimatedCost))}
                          </strong>
                        </Typography>
                      )}
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <EstimatePricingFab
                      leadId={
                        selectedLead?.id ||
                        selectedLead?._id ||
                        quote?.leadId ||
                        null
                      }
                      quoteId={quoteId}
                      formSnapshot={{
                        from: formData.from,
                        to: formData.to,
                        serviceType: formData.serviceType,
                        items: formData.lineItems,
                        itemsList: formData.items,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      saving ? <CircularProgress size={20} /> : <Save />
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Update Quote"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      <CreateProductModal
        open={createProductModal.open}
        onClose={closeCreateProductModal}
        title="Create Product from Line Item"
        initialValues={{
          name: createProductModal.name,
          description: createProductModal.name,
          unitPrice:
            createProductModal.index !== null
              ? Number(formData.lineItems[createProductModal.index]?.unitPrice || 0)
              : 0,
          defaultSupplier:
            createProductModal.index !== null
              ? formData.lineItems[createProductModal.index]?.supplier || "bubakii"
              : "bubakii",
          category: "services",
          active: true,
        }}
        onUseAsOneOff={() => {
          if (createProductModal.index === null) return;
          handleCustomItem(createProductModal.index)(createProductModal.name);
        }}
        onCreated={(product) => {
          if (createProductModal.index === null) return;
          handleProductSelect(createProductModal.index)(product);
        }}
      />
    </Box>
  );
};

export default QuoteEditView;
